import { ApiError, ForbiddenError } from "@/api/errors";

import { CreateModelError } from "./use-create-model";

/** モデル作成の失敗を、画面が文言を選べる形にしたもの。 */
export type CreateErrorView = {
  /** 失敗の理由。`failed` は理由を特定できないもの。 */
  readonly reason:
    | "duplicate"
    | "forbidden"
    | "group-missing"
    | "file-invalid"
    | "failed";
  /** 失敗した手順（重複がグループ id かモデル名かの出し分けに使う）。 */
  readonly stage: CreateModelError["stage"] | null;
  /** backend の説明（BPMN を受け付けられなかったときの理由。英語のまま）。 */
  readonly detail: string | null;
  /** 同じ入力のまま押し直せば通る見込みがあるか（理由を特定できない失敗だけ）。 */
  readonly retryable: boolean;
  /** この操作で作れたグループ。画面はグループ欄をこれに切り替える。 */
  readonly createdGroup: CreateModelError["createdGroup"];
  /** 作りかけのモデルの行方。 */
  readonly model:
    | { readonly kind: "not-created" }
    | { readonly kind: "rolled-back" }
    | { readonly kind: "left-over"; readonly id: string };
};

/**
 * 作成の失敗を分類する。
 *
 * 失敗全般を 1 つの文言にまとめると、グループが無いだけの失敗を権限の問題と
 * 誤解させる（#41 の発端）。backend の応答（status / error_code / message）から
 * 理由を特定し、押し直しても通らない失敗には「もう一度」を促さない。
 */
export function describeCreateError(error: unknown): CreateErrorView {
  if (!(error instanceof CreateModelError)) {
    return {
      reason: "failed",
      stage: null,
      detail: null,
      retryable: true,
      createdGroup: null,
      model: { kind: "not-created" },
    };
  }

  const reason = reasonOf(error.cause);
  return {
    reason,
    stage: error.stage,
    detail:
      reason === "file-invalid" && error.cause instanceof ApiError
        ? error.cause.message
        : null,
    retryable: reason === "failed",
    createdGroup: error.createdGroup,
    model: modelOf(error),
  };
}

function modelOf(error: CreateModelError): CreateErrorView["model"] {
  // モデルが作れているのは、ファイル追加の手順で失敗したときだけ
  if (error.stage !== "files") return { kind: "not-created" };
  if (error.leftoverModelId === null) return { kind: "rolled-back" };
  return { kind: "left-over", id: error.leftoverModelId };
}

function reasonOf(cause: unknown): CreateErrorView["reason"] {
  if (cause instanceof ForbiddenError) return "forbidden";
  if (!(cause instanceof ApiError)) return "failed";

  if (cause.status === 400 && cause.errorCode.endsWith("_already_exists")) {
    return "duplicate";
  }
  // BPMN の検証で弾かれた（プロセス id が他のモデルと重なっている等）
  if (cause.errorCode === "process_model_file_invalid") return "file-invalid";
  // SpiffArena はグループが無いと ApiError でない例外を投げ、500 の本文に
  // 例外名と `process_group_not_found` を入れて返す
  if (cause.message.includes("process_group_not_found")) {
    return "group-missing";
  }
  return "failed";
}
